import { useState } from 'react'
import { useShowDialog } from 'App'
import type { Instance } from 'mobx-state-tree'
import type { CenterSummaryModel } from 'models/CenterSettings/CenterModel'
import { PrimaryButton, SecondaryButton } from '../Button/Button'
import { Select, type SelectOption } from '../Select/Select'
import { Column, Row } from '../Table/Table'
import { BodyText, Heading1 } from '../Typography/Typography'

interface TransferredVehicle {
    id: number
    name: string | null
    center_id: number
}

// The vehicle's name and number when there is one vehicle
const getVehiclesDescription = (vehicles: ReadonlyArray<TransferredVehicle>) => {
    const [singleVehicle] = vehicles
    return vehicles.length === 1 && singleVehicle !== undefined
        ? `vehicle ${singleVehicle.name ?? ''} (${singleVehicle.id})`
        : `${vehicles.length} vehicles`
}

interface TransferVehicleContentProps {
    vehicles: ReadonlyArray<TransferredVehicle>
    centerOptions: Array<SelectOption>
    selection: { deploymentId: number | null }
    onConfirmed: VoidFunction
    onCancelled: VoidFunction
}

const TransferVehicleContent = ({ vehicles, centerOptions, selection, onConfirmed, onCancelled }: TransferVehicleContentProps) => {
    const [selectedOptions, setSelectedOptions] = useState<Array<SelectOption>>([])
    const showDialog = useShowDialog()

    const hideDialog = () => { showDialog(null) }

    return (
        <Column style={{ alignItems: 'center', gap: '1.5em', width: '100%', wordBreak: 'break-word'}}>
            <Heading1>{ vehicles.length === 1 ? 'TRANSFER VEHICLE' : 'TRANSFER VEHICLES' }</Heading1>
            <BodyText style={{ textAlign: 'center' }}>choose destination center for { getVehiclesDescription(vehicles) }:</BodyText>
            <Select {...{
                name: 'center',
                options: centerOptions,
                selectedOptions,
                label: 'Select center',
                style: { width: '100%' },
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value
                    selection.deploymentId = Number(value)
                    setSelectedOptions(centerOptions.filter(o => o.value === value))
                },
            }} />
            <Row style={{ marginTop: '1em', gap: '3em' }}>
                <SecondaryButton {...{
                    text: 'Cancel',
                    style: { minInlineSize: '150px' },
                    onClick: () => { hideDialog(); onCancelled() },
                }} />
                <PrimaryButton {...{
                    text: 'Transfer',
                    style: { minInlineSize: '150px' },
                    isDisabled: selectedOptions.length === 0,
                    onClick: () => { hideDialog(); onConfirmed() },
                }} />
            </Row>
        </Column>
    )
}

interface ShowTransferVehicleDialogParams {
    showDialog: ReturnType<typeof useShowDialog>
    vehicles: ReadonlyArray<TransferredVehicle>
    centers: Array<Instance<typeof CenterSummaryModel>>
    assignToDeployment: (vehicleId: number, centerId: number) => Promise<void>
    // Called once the transfer was requested
    onTransferred?: VoidFunction
}

// Opens the popup for transferring one vehicle or more to another center
// The vehicles' center is not offered when they are all in the same center. Vehicles already in the chosen center are
// not transferred.
export function showTransferVehicleDialog({ showDialog, vehicles, centers, assignToDeployment, onTransferred }: ShowTransferVehicleDialogParams) {
    const selection = { deploymentId: null as number | null }

    const [firstVehicle] = vehicles
    const sharedCenterId = firstVehicle !== undefined && vehicles.every(v => v.center_id === firstVehicle.center_id) ? firstVehicle.center_id : null
    const centerOptions: Array<SelectOption> = centers.filter(c => c.id !== sharedCenterId).map(c => ({
        value: c.id.toString(),
        text: `${c.id} - ${c.name}`,
    }))

    showDialog({
        content: (
            <TransferVehicleContent
                vehicles={vehicles}
                centerOptions={centerOptions}
                selection={selection}
                onCancelled={() => {}}
                onConfirmed={() => {
                    const { deploymentId } = selection
                    if (deploymentId !== null) {
                        vehicles
                            .filter(vehicle => vehicle.center_id !== deploymentId)
                            .forEach(vehicle => { void assignToDeployment(vehicle.id, deploymentId) })
                        onTransferred?.()
                    }
                }}
            />
        ),
    })
}
